import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';

// Detect backend URL automatically (same logic as AuthContext)
const getBackendURL = () => {
  // If REACT_APP_BACKEND_URL is set, use it (for local development)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Otherwise, use the current domain (works for both fadaapp.com and preview URL)
  return window.location.origin;
};

const PageTransition = ({ children }) => {
  const [showLoader, setShowLoader] = useState(true);
  const [apps, setApps] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    // Buscar apps (primeiro tenta featured, se não tiver, pega qualquer um com logo)
    fetch(`${getBackendURL()}/api/apps`)
      .then(res => res.json())
      .then(data => {
        // Ensure data is an array
        if (!Array.isArray(data)) {
          console.error('❌ Error loading apps: data is not an array', data);
          setApps([]);
          return;
        }
        
        // Pegar apps com logo primeiro
        const appsWithLogo = data.filter(app => app.logo_url);
        
        // Se tiver featured, usar eles
        const featured = appsWithLogo.filter(app => app.featured);
        
        if (featured.length >= 3) {
          setApps(featured.slice(0, 10));
        } else if (appsWithLogo.length >= 3) {
          // Se não tiver featured suficiente, usar qualquer um com logo
          setApps(appsWithLogo.slice(0, 10));
        } else {
          setApps([]);
        }
        
        console.log('✅ Apps loaded for loading screen:', appsWithLogo.length);
      })
      .catch((err) => {
        console.error('❌ Error loading apps:', err);
        setApps([]);
      });

    // Esconder loader após 1.5 segundos
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  // Rotação automática dos apps
  useEffect(() => {
    if (apps.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % apps.length);
      }, 800); // Troca a cada 800ms
      return () => clearInterval(interval);
    }
  }, [apps.length]);

  // Pegar 3 apps para mostrar
  const getVisibleApps = () => {
    if (apps.length === 0) return [];
    if (apps.length === 1) return [apps[0], apps[0], apps[0]];
    if (apps.length === 2) return [apps[currentIndex % 2], apps[(currentIndex + 1) % 2], apps[currentIndex % 2]];
    
    return [
      apps[currentIndex % apps.length],
      apps[(currentIndex + 1) % apps.length],
      apps[(currentIndex + 2) % apps.length],
    ];
  };

  const visibleApps = getVisibleApps();

  return (
    <>
      {/* Loader com blur TRANSPARENTE e fade suave */}
      <AnimatePresence>
        {showLoader && (
          <motion.div
            initial={{ opacity: 1, backdropFilter: 'blur(20px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)', // Quase transparente
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="text-center"
            >
              {/* Animação circular com logos dos apps */}
              {visibleApps.length > 0 ? (
                <div className="relative w-40 h-40 mx-auto mb-6">
                  {/* App 1 - Topo */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`app-0-${currentIndex}`}
                      initial={{ scale: 0, opacity: 0, y: 20 }}
                      animate={{ scale: 1, opacity: 1, y: 0 }}
                      exit={{ scale: 0, opacity: 0, y: -20 }}
                      transition={{ duration: 0.4 }}
                      className="absolute top-0 left-1/2 -translate-x-1/2"
                    >
                      <div className="w-20 h-20 rounded-2xl bg-white shadow-2xl p-3 ring-4 ring-purple-400">
                        <img
                          src={visibleApps[0].logo_url}
                          alt={visibleApps[0].name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* App 2 - Direita */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`app-1-${currentIndex}`}
                      initial={{ scale: 0.6, opacity: 0, x: -30 }}
                      animate={{ scale: 0.85, opacity: 0.7, x: 0 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 }}
                      className="absolute bottom-4 right-0"
                    >
                      <div className="w-16 h-16 rounded-xl bg-white shadow-xl p-2">
                        <img
                          src={visibleApps[1].logo_url}
                          alt={visibleApps[1].name}
                          className="w-full h-full object-contain opacity-80"
                        />
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* App 3 - Esquerda */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`app-2-${currentIndex}`}
                      initial={{ scale: 0.6, opacity: 0, x: 30 }}
                      animate={{ scale: 0.85, opacity: 0.7, x: 0 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 }}
                      className="absolute bottom-4 left-0"
                    >
                      <div className="w-16 h-16 rounded-xl bg-white shadow-xl p-2">
                        <img
                          src={visibleApps[2].logo_url}
                          alt={visibleApps[2].name}
                          className="w-full h-full object-contain opacity-80"
                        />
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* Círculo de fundo animado */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 border-4 border-dashed border-purple-300 rounded-full opacity-20"
                  />
                </div>
              ) : (
                // Fallback: spinner moderno
                <div className="relative w-20 h-20 mx-auto mb-6">
                  <motion.div
                    className="absolute inset-0 rounded-full border-4 border-gray-300"
                  ></motion.div>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0 rounded-full border-4 border-transparent border-t-purple-500 border-r-pink-500"
                  ></motion.div>
                </div>
              )}
              
              {/* Texto animado */}
              <motion.p
                animate={{
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                }}
                className="text-gray-800 dark:text-white text-xl font-bold drop-shadow-lg"
              >
                Carregando
              </motion.p>
              
              {/* Pontos animados */}
              <div className="flex justify-center gap-2 mt-3">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{
                      scale: [1, 1.4, 1],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 0.8,
                      delay: i * 0.2,
                      repeat: Infinity,
                    }}
                    className="w-2 h-2 bg-purple-600 rounded-full drop-shadow-lg"
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Conteúdo da página - SEM animação de entrada */}
      <div>
        {children}
      </div>
    </>
  );
};

export default PageTransition;
